import { Injectable, signal, inject } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, take, tap } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  GlobalMessageEntities,
  GlobalMessageType,
  Translatable,
} from '@boels/models';
import { isNotUndefined } from '@boels/helpers';
import { GlobalMessageState } from '../reducers/global-message.reducer';
import {
  addMessage,
  removeMessage,
  removeMessagesByType,
} from '../actions/global-message.actions';
import { getGlobalMessageEntities } from '../selectors/global-message.selectors';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root',
})
export class GlobalMessageFacade {
  protected store = inject<Store<GlobalMessageState>>(Store);
  protected translocoService = inject(TranslocoService);

  private _preserveOnRedirect = signal<boolean>(false);

  get preserveOnRedirect(): boolean {
    return this._preserveOnRedirect();
  }

  set preserveOnRedirect(value: boolean) {
    this._preserveOnRedirect.set(value);
  }

  /**
   * Get all global messages
   */
  get(): Observable<GlobalMessageEntities> {
    return this.store.pipe(
      select(getGlobalMessageEntities),
      filter(isNotUndefined),
    );
  }

  /**
   * Add one message into store
   * @param message: string | Translatable
   * @param type: GlobalMessageType object
   */
  add(
    message: string | Translatable,
    type: GlobalMessageType,
    preserveOnRedirect = false,
  ): void {
    this.preserveOnRedirect = preserveOnRedirect;
    if (typeof message === 'string') {
      this.store.dispatch(
        addMessage({
          message: { text: message, type },
        }),
      );
    } else if (message.key !== undefined) {
      this.translocoService
        .selectTranslate(message.key, message.params, message.scope)
        .pipe(
          take(1),
          tap((text) => {
            this.store.dispatch(
              addMessage({
                message: { text, type },
              }),
            );
          }),
        )
        .subscribe();
    }
  }

  /**
   * Remove message(s) from store
   * @param messageType: GlobalMessageType
   * @param index:optional. Without it, messages will be removed by type; otherwise,
   * message will be removed from list by index.
   */
  remove(messageType: GlobalMessageType, index?: number): void {
    this.store.dispatch(
      index !== undefined
        ? removeMessage({
            messageType,
            index,
          })
        : removeMessagesByType({ messageType }),
    );
  }
}
